<script setup>
import { defineProps, defineEmits, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ToolbarActions from './editor/DropdownActions.vue';
import ToolbarAction from './editor/Action.vue';

import { EditorContent, useEditor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import FileHandler from '@tiptap/extension-file-handler';
import Image from '@tiptap/extension-image';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { cleanHtmlContent } from 'shared/helpers/cleanHtmlContent';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);
const store = useStore();
const { t } = useI18n();

const readOnly = ref(props.disabled);

const colors = [
  '#E10600',
  '#E91E63',
  '#8A2BE2',
  '#001F5B',
  '#4CAF50',
  '#2196F3',
  '#FF9800',
  '#FFEB3B',
];

let isInternalUpdate = false;

const editor = useEditor({
  content: props.modelValue,
  extensions: [
    StarterKit,
    Color,
    TextStyle,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Image.configure({
      resize: {
        enabled: true,
        minWidth: 50,
        minHeight: 50,
        directions: ['bottom-right'],
        alwaysPreserveAspectRatio: true,
      },
    }),
    FileHandler.configure({
      allowedMimeTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
      onDrop: (currentEditor, files, pos) => {
        files.forEach(file => {
          const fileReader = new FileReader();
          fileReader.readAsDataURL(file);
          fileReader.onload = () => {
            currentEditor
              .chain()
              .insertContentAt(pos, {
                type: 'image',
                attrs: {
                  src: fileReader.result,
                },
              })
              .focus()
              .run();
          };
        });
      },
      onPaste: (currentEditor, files, event) => {
        if (event !== '') return;
        files.forEach(file => {
          const fileReader = new FileReader();
          fileReader.readAsDataURL(file);
          fileReader.onload = () => {
            currentEditor
              .chain()
              .insertContentAt(currentEditor.state.selection.anchor, {
                type: 'image',
                attrs: {
                  src: fileReader.result,
                },
              })
              .focus()
              .run();
          };
        });
      },
    }),
  ],
  onUpdate: ({ editor: updatedEditor }) => {
    const cleaned = cleanHtmlContent(updatedEditor.getHTML());
    isInternalUpdate = true;
    emit('update:modelValue', cleaned);
  },
});

const uploadImage = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/png, image/jpeg, image/gif, image/webp';
  input.onchange = async () => {
    const files = input.files;
    if (files && files.length > 0) {
      const file = files[0];
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await store.dispatch('editorFiles/create', formData);
        editor.value.chain().focus().setImage({ src: response.file_url }).run();
      } catch (error) {
        const errorMessage =
          error?.message || t('EDITOR_FILE_MGMT.FILE_UPLOAD.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    }
  };
  input.click();
};

const addLinkPrompt = () => {
  const linkElement = editor.value.chain().focus().extendMarkRange('link');
  const currentLink = editor.value.getAttributes('link').href;
  /* eslint-disable no-alert */
  const url = prompt('Enter the URL', currentLink);
  /* eslint-enable no-alert */
  const finalURL = url?.trim();

  if (finalURL === null || finalURL === '') {
    linkElement.unsetLink().run();
  } else {
    linkElement.setLink({ href: finalURL }).run();
  }
};

watch(
  () => props.modelValue,
  newValue => {
    if (isInternalUpdate) {
      isInternalUpdate = false;
      return;
    }
    if (editor && newValue !== editor.value.getHTML()) {
      editor.value.commands?.setContent(newValue || '', false);
    }
  }
);

watch(
  () => props.disabled,
  newValue => {
    readOnly.value = newValue;
  }
);

function insertContent(htmlContent) {
  if (editor) {
    const cleaned = cleanHtmlContent(htmlContent);
    editor.value.commands?.insertContent(cleaned);
  }
}

defineExpose({
  insertContent,
});
</script>

<template>
  <div v-if="editor" class="flex flex-col h-full py-0 relative">
    <div
      class="flex flex-auto min-h-[150px] overflow-y-auto pb-20 !inline-block"
    >
      <div
        v-if="readOnly"
        class="grow html-editor email-preview"
        v-html="modelValue"
      />
      <EditorContent
        v-else
        :editor="editor"
        class="grow html-editor email-preview"
      />
      <slot name="signature" />
      <slot name="attachments" />
    </div>
    <div
      v-if="!readOnly"
      class="absolute shadow-sm shadow-black-600 bottom-2 flex w-fit gap-1 rounded px-2 py-1 rounded-full left-1/2 -translate-x-1/2 bg-n-slate-7 bg-opacity-55 backdrop-blur-sm"
    >
      <ToolbarAction
        icon="i-lucide-undo"
        :is-disabled="!editor.can().undo()"
        @click="editor.chain().focus().undo().run()"
      />
      <ToolbarAction
        icon="i-lucide-redo"
        :is-disabled="!editor.can().redo()"
        @click="editor.chain().focus().redo().run()"
      />
      <div class="border-l h-6 self-center border-n-slate-8" />
      <ToolbarAction
        icon="i-lucide-bold"
        :is-active="editor.isActive('bold')"
        @click="editor.chain().focus().toggleBold().run()"
      />
      <ToolbarAction
        icon="i-lucide-italic"
        :is-active="editor.isActive('italic')"
        @click="editor.chain().focus().toggleItalic().run()"
      />
      <ToolbarAction
        icon="i-lucide-strikethrough"
        :is-active="editor.isActive('strike')"
        @click="editor.chain().focus().toggleStrike().run()"
      />
      <ToolbarAction
        icon="i-lucide-underline"
        :is-active="editor.isActive('underline')"
        @click="editor.chain().focus().toggleUnderline().run()"
      />
      <ToolbarActions icon="i-lucide-paint-bucket">
        <ToolbarAction
          v-for="color in colors"
          :key="color"
          :style="{ color: color, 'border-color': color }"
          icon="i-lucide-type"
          class="rounded-full border border-solid m-1 !p-0 !w-6 !h-6"
          @click="editor.chain().focus().setColor(color).run()"
        />
        <ToolbarAction
          class="rounded-full border border-solid m-1 !p-0 !w-6 !h-6"
          icon="i-lucide-x"
          @click="editor.chain().focus().unsetColor().run()"
        />
      </ToolbarActions>
      <div class="border-l h-6 self-center border-n-slate-8" />
      <ToolbarActions icon="i-lucide-text-initial">
        <ToolbarAction
          icon="i-lucide-text-initial"
          :is-active="editor.isActive('paragraph')"
          @click="editor.chain().focus().setParagraph().run()"
        />
        <ToolbarAction
          icon="i-lucide-heading-1"
          :is-active="editor.isActive('heading', { level: 1 })"
          @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
        />
        <ToolbarAction
          icon="i-lucide-heading-2"
          :is-active="editor.isActive('heading', { level: 2 })"
          @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
        />
        <ToolbarAction
          icon="i-lucide-heading-3"
          :is-active="editor.isActive('heading', { level: 3 })"
          @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
        />
        <div class="border-l my-1 border-n-slate-8 mx-1" />
        <ToolbarAction
          icon="i-lucide-align-left"
          :is-active="editor.isActive({ textAlign: 'left' })"
          @click="editor.chain().focus().unsetTextAlign().run()"
        />
        <ToolbarAction
          icon="i-lucide-align-center"
          :is-active="editor.isActive({ textAlign: 'center' })"
          @click="editor.chain().focus().toggleTextAlign('center').run()"
        />
        <ToolbarAction
          icon="i-lucide-align-right"
          :is-active="editor.isActive({ textAlign: 'right' })"
          @click="editor.chain().focus().toggleTextAlign('right').run()"
        />
        <ToolbarAction
          icon="i-lucide-align-justify"
          :is-active="editor.isActive({ textAlign: 'justify' })"
          @click="editor.chain().focus().toggleTextAlign('justify').run()"
        />
      </ToolbarActions>
      <ToolbarAction
        icon="i-lucide-list"
        :is-active="editor.isActive('bulletList')"
        @click="editor.chain().focus().toggleBulletList().run()"
      />
      <ToolbarAction
        icon="i-lucide-list-ordered"
        :is-active="editor.isActive('orderedList')"
        @click="editor.chain().focus().toggleOrderedList().run()"
      />
      <ToolbarAction
        icon="i-lucide-indent-increase"
        :is-disabled="!editor.isActive('listItem')"
        @click="editor.chain().focus().sinkListItem('listItem').run()"
      />
      <ToolbarAction
        icon="i-lucide-indent-decrease"
        :is-disabled="!editor.isActive('listItem')"
        @click="editor.chain().focus().liftListItem('listItem').run()"
      />
      <ToolbarActions icon="i-lucide-more-vertical">
        <ToolbarAction
          icon="i-lucide-minus"
          @click="editor.chain().focus().setHorizontalRule().run()"
        />
        <ToolbarAction
          icon="i-lucide-quote"
          :is-active="editor.isActive('blockquote')"
          @click="editor.chain().focus().toggleBlockquote().run()"
        />
        <ToolbarAction
          icon="i-lucide-square-code"
          :is-active="editor.isActive('codeBlock')"
          @click="editor.chain().focus().toggleCodeBlock().run()"
        />
        <ToolbarAction
          icon="i-lucide-code"
          :is-active="editor.isActive('code')"
          @click="editor.chain().focus().toggleCode().run()"
        />
      </ToolbarActions>
      <div class="border-l h-6 self-center border-n-slate-8" />
      <ToolbarAction
        icon="i-lucide-link-2"
        :is-active="editor.isActive('link')"
        @click="addLinkPrompt"
      />
      <ToolbarAction
        icon="i-lucide-link-2-off"
        :is-active="editor.isActive('link')"
        :is-disabled="!editor.isActive('link')"
        @click="
          editor.chain().focus().extendMarkRange('link').unsetLink().run()
        "
      />
      <ToolbarAction
        icon="i-lucide-image"
        :is-disabled="editor.isActive('image')"
        @click="uploadImage"
      />
      <div class="border-l h-6 self-center border-n-slate-8" />
      <ToolbarAction
        icon="i-lucide-eraser"
        @click="editor.chain().focus().clearNodes().unsetAllMarks().run()"
      />
    </div>
  </div>
  <div v-else>
    <Icon icon="i-lucide-loader-2" class="animate-spin text-n-slate-400" />
  </div>
</template>

<style lang="scss">
.html-editor {
  @apply w-full h-full inline-block;

  .tiptap.ProseMirror {
    @apply w-full h-full;
  }
}

.email-preview {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
  padding: 0;

  p {
    margin: 16px 0;
  }

  h1 {
    font-size: 24px;
    font-weight: bold;
    margin: 16px 0;
  }

  h2 {
    font-size: 20px;
    font-weight: bold;
    margin: 16px 0;
  }

  h3 {
    font-size: 16px;
    font-weight: bold;
    margin: 16px 0;
  }

  a {
    color: #1a0dab;
    text-decoration: underline;
  }

  ul,
  ol {
    margin: 16px 0;
    padding-left: 40px;
  }
  ol {
    list-style-type: decimal;
  }

  img {
    max-width: 100%;
    height: auto;
    display: inline-block;
    vertical-align: baseline;
    border: none;
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;

    td {
      vertical-align: top;
    }
  }
}

[data-resize-state='true'] [data-resize-wrapper],
[data-resize-wrapper]:hover {
  @apply rounded-sm cursor-move;
  outline: 1px solid rgb(var(--blue-9));

  [data-resize-handle] {
    @apply block;
  }
}

[data-resize-handle] {
  @apply hidden absolute z-10 rounded-lg;
  background-color: rgb(var(--blue-9));

  &[data-resize-handle='bottom-right'] {
    width: 10px;
    height: 10px;
    bottom: -4px !important;
    right: -4px !important;
    background-color: rgb(var(--blue-9));
    cursor: nwse-resize;
  }
}
</style>
